import { useState, useCallback, useRef, useEffect } from 'react'
import { ChevronUp, ChevronDown } from 'lucide-react'
import { ClockIcon, ChevronDownIcon } from '@/components/icons'
import { cn } from '@/lib/utils'
import { format, parse, isSameDay, isToday, startOfDay } from 'date-fns'

const TimePickerComponent = ({ value, onChange, onCancel, selectedDate }) => {
    // Helper function to check if a time is in the past for today (with 5-minute buffer)
    const isTimeInPast = useCallback(
        (hours, minutes, ampm) => {
            if (!selectedDate) return false

            // Check if selected date is today
            const parsedDate = selectedDate ? parse(selectedDate, 'yyyy-MM-dd', new Date()) : new Date()
            const isSelectedDateToday = isToday(parsedDate)

            if (!isSelectedDateToday) return false

            // Convert to 24-hour format for comparison
            const hours24 = ampm === 'PM' ? (hours === 12 ? 12 : hours + 12) : hours === 12 ? 0 : hours

            // Get current time with 5-minute buffer
            const now = new Date()
            const bufferTime = new Date(now.getTime() + 5 * 60 * 1000) // Add 5 minutes
            const bufferHours = bufferTime.getHours()
            const bufferMinutes = bufferTime.getMinutes()

            // Compare times with buffer
            if (hours24 < bufferHours) return true
            if (hours24 === bufferHours && minutes < bufferMinutes) return true

            return false
        },
        [selectedDate],
    )

    const [time, setTime] = useState(() => {
        if (value && typeof value === 'string') {
            // Handle time string format like "14:30" or "2:30 PM"
            const timeMatch = value.match(/(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i)
            if (timeMatch) {
                const [, hourStr, minuteStr, ampmStr] = timeMatch
                let hours = parseInt(hourStr, 10)
                const minutes = parseInt(minuteStr, 10)
                let ampm = ampmStr ? ampmStr.toUpperCase() : hours >= 12 ? 'PM' : 'AM'

                // Convert to 12-hour format for display
                if (!ampmStr) {
                    // If no AM/PM specified, assume 24-hour format
                    ampm = hours >= 12 ? 'PM' : 'AM'
                    hours = hours % 12 || 12
                } else {
                    // Already in 12-hour format
                    hours = hours === 0 ? 12 : hours
                }

                return {
                    hours: isNaN(hours) ? 12 : hours,
                    minutes: isNaN(minutes) ? 0 : minutes,
                    ampm,
                }
            }
        }
        return { hours: 12, minutes: 0, ampm: 'AM' }
    })

    const increment = useCallback(
        (field) => {
            setTime((prev) => {
                const newTime = { ...prev }
                if (field === 'hours') {
                    const proposedHours = prev.hours === 12 ? 1 : prev.hours + 1
                    // Check if the new time would be in the past
                    if (isTimeInPast(proposedHours, prev.minutes, prev.ampm)) {
                        return prev // Don't change if it would create a past time
                    }
                    newTime.hours = proposedHours
                } else if (field === 'minutes') {
                    const proposedMinutes = prev.minutes === 59 ? 0 : prev.minutes + 1
                    // Check if the new time would be in the past
                    if (isTimeInPast(prev.hours, proposedMinutes, prev.ampm)) {
                        return prev // Don't change if it would create a past time
                    }
                    newTime.minutes = proposedMinutes
                } else if (field === 'ampm') {
                    const proposedAmpm = prev.ampm === 'AM' ? 'PM' : 'AM'
                    // Check if the new time would be in the past
                    if (isTimeInPast(prev.hours, prev.minutes, proposedAmpm)) {
                        return prev // Don't change if it would create a past time
                    }
                    newTime.ampm = proposedAmpm
                }
                return newTime
            })
        },
        [isTimeInPast],
    )

    const decrement = useCallback(
        (field) => {
            setTime((prev) => {
                const newTime = { ...prev }
                if (field === 'hours') {
                    const proposedHours = prev.hours === 1 ? 12 : prev.hours - 1
                    // Check if the new time would be in the past
                    if (isTimeInPast(proposedHours, prev.minutes, prev.ampm)) {
                        return prev // Don't change if it would create a past time
                    }
                    newTime.hours = proposedHours
                } else if (field === 'minutes') {
                    const proposedMinutes = prev.minutes === 0 ? 59 : prev.minutes - 1
                    // Check if the new time would be in the past
                    if (isTimeInPast(prev.hours, proposedMinutes, prev.ampm)) {
                        return prev // Don't change if it would create a past time
                    }
                    newTime.minutes = proposedMinutes
                } else if (field === 'ampm') {
                    const proposedAmpm = prev.ampm === 'AM' ? 'PM' : 'AM'
                    // Check if the new time would be in the past
                    if (isTimeInPast(prev.hours, prev.minutes, proposedAmpm)) {
                        return prev // Don't change if it would create a past time
                    }
                    newTime.ampm = proposedAmpm
                }
                return newTime
            })
        },
        [isTimeInPast],
    )

    const handleSave = useCallback(() => {
        const { hours, minutes, ampm } = time

        // Prevent saving past times
        if (isTimeInPast(hours, minutes, ampm)) {
            return
        }

        const date = new Date()
        const hours24 = ampm === 'PM' ? (hours === 12 ? 12 : hours + 12) : hours === 12 ? 0 : hours
        date.setHours(hours24, minutes, 0, 0)
        onChange(date.toTimeString().slice(0, 5))
    }, [time, onChange, isTimeInPast])

    const formatNumber = (num) => {
        const validNum = isNaN(num) ? 0 : num
        return validNum.toString().padStart(2, '0')
    }

    return (
        <div
            className="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:z-50 tw:bg-[#071727] tw:rounded-[2px] tw:p-3 tw:w-[370px] tw:border tw:border-[#3B5168]"
            style={{ boxShadow: '0px 4px 24px 0px #05111D40' }}
        >
            <div className="tw:flex tw:items-center tw:justify-center tw:gap-4 tw:mb-8">
                {/* Hours */}
                <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
                    <button
                        onClick={() => increment('hours')}
                        disabled={
                            selectedDate &&
                            isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                            isTimeInPast(time.hours === 12 ? 1 : time.hours + 1, time.minutes, time.ampm)
                        }
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white tw:transition-colors',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours === 12 ? 1 : time.hours + 1, time.minutes, time.ampm)
                                ? 'tw:opacity-50 tw:cursor-not-allowed'
                                : 'hover:tw:bg-[#2450BF]/30',
                        )}
                    >
                        <ChevronUp size={16} />
                    </button>
                    <div
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#040D16] tw:border tw:border-[#3B5168] tw:rounded tw:flex tw:items-center tw:justify-center tw:font-medium',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours, time.minutes, time.ampm)
                                ? 'tw:text-gray-500 tw:opacity-50'
                                : 'tw:text-white',
                        )}
                    >
                        {formatNumber(time.hours)}
                    </div>
                    <button
                        onClick={() => decrement('hours')}
                        disabled={
                            selectedDate &&
                            isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                            isTimeInPast(time.hours === 1 ? 12 : time.hours - 1, time.minutes, time.ampm)
                        }
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white tw:transition-colors',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours === 1 ? 12 : time.hours - 1, time.minutes, time.ampm)
                                ? 'tw:opacity-50 tw:cursor-not-allowed'
                                : 'hover:tw:bg-[#2450BF]/30',
                        )}
                    >
                        <ChevronDown size={16} />
                    </button>
                </div>

                <div className="tw:text-white tw:text-xl tw:font-medium">:</div>

                {/* Minutes */}
                <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
                    <button
                        onClick={() => increment('minutes')}
                        disabled={
                            selectedDate &&
                            isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                            isTimeInPast(time.hours, time.minutes === 59 ? 0 : time.minutes + 1, time.ampm)
                        }
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white tw:transition-colors',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours, time.minutes === 59 ? 0 : time.minutes + 1, time.ampm)
                                ? 'tw:opacity-50 tw:cursor-not-allowed'
                                : 'hover:tw:bg-[#2450BF]/30',
                        )}
                    >
                        <ChevronUp size={16} />
                    </button>
                    <div
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#040D16] tw:border tw:border-[#3B5168] tw:rounded tw:flex tw:items-center tw:justify-center tw:font-medium',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours, time.minutes, time.ampm)
                                ? 'tw:text-gray-500 tw:opacity-50'
                                : 'tw:text-white',
                        )}
                    >
                        {formatNumber(time.minutes)}
                    </div>
                    <button
                        onClick={() => decrement('minutes')}
                        disabled={
                            selectedDate &&
                            isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                            isTimeInPast(time.hours, time.minutes === 0 ? 59 : time.minutes - 1, time.ampm)
                        }
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white tw:transition-colors',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours, time.minutes === 0 ? 59 : time.minutes - 1, time.ampm)
                                ? 'tw:opacity-50 tw:cursor-not-allowed'
                                : 'hover:tw:bg-[#2450BF]/30',
                        )}
                    >
                        <ChevronDown size={16} />
                    </button>
                </div>

                {/* AM/PM */}
                <div className="tw:flex tw:flex-col tw:items-center tw:gap-2">
                    <button
                        onClick={() => increment('ampm')}
                        disabled={
                            selectedDate &&
                            isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                            isTimeInPast(time.hours, time.minutes, time.ampm === 'AM' ? 'PM' : 'AM')
                        }
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white tw:transition-colors',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours, time.minutes, time.ampm === 'AM' ? 'PM' : 'AM')
                                ? 'tw:opacity-50 tw:cursor-not-allowed'
                                : 'hover:tw:bg-[#2450BF]/30',
                        )}
                    >
                        <ChevronUp size={16} />
                    </button>
                    <div
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#040D16] tw:border tw:border-[#3B5168] tw:rounded tw:flex tw:items-center tw:justify-center tw:font-medium tw:text-sm',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours, time.minutes, time.ampm)
                                ? 'tw:text-gray-500 tw:opacity-50'
                                : 'tw:text-white',
                        )}
                    >
                        {time.ampm}
                    </div>
                    <button
                        onClick={() => decrement('ampm')}
                        disabled={
                            selectedDate &&
                            isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                            isTimeInPast(time.hours, time.minutes, time.ampm === 'AM' ? 'PM' : 'AM')
                        }
                        className={cn(
                            'tw:w-8 tw:h-8 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:rounded tw:flex tw:items-center tw:justify-center tw:text-white tw:transition-colors',
                            selectedDate &&
                                isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                                isTimeInPast(time.hours, time.minutes, time.ampm === 'AM' ? 'PM' : 'AM')
                                ? 'tw:opacity-50 tw:cursor-not-allowed'
                                : 'hover:tw:bg-[#2450BF]/30',
                        )}
                    >
                        <ChevronDown size={16} />
                    </button>
                </div>
            </div>

            <div className="tw:flex tw:gap-2">
                <button
                    onClick={onCancel}
                    className="tw:flex-1 tw:h-7 tw:bg-[#2450BF33] tw:border tw:border-[#2450BF] tw:text-white tw:!text-[13px] tw:!font-medium tw:rounded tw:hover:bg-[#2450BF]/30 tw:transition-colors"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={
                        selectedDate &&
                        isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                        isTimeInPast(time.hours, time.minutes, time.ampm)
                    }
                    className={cn(
                        'tw:flex-1 tw:h-7 tw:text-white tw:!text-[13px] tw:font-medium tw:rounded tw:transition-colors tw:border-0',
                        selectedDate &&
                            isToday(parse(selectedDate, 'yyyy-MM-dd', new Date())) &&
                            isTimeInPast(time.hours, time.minutes, time.ampm)
                            ? 'tw:bg-gray-600 tw:cursor-not-allowed tw:opacity-50'
                            : 'tw:bg-[#2450BF] hover:tw:bg-[#2450BF]/90',
                    )}
                >
                    Save
                </button>
            </div>
        </div>
    )
}

const TimePicker = ({ value, onChange, placeholder = '12:00 AM', className, selectedDate, ...props }) => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef(null)
    const [displayValue, setDisplayValue] = useState(value || placeholder)

    // Helper function to check if a time string is in the past for today (with 5-minute buffer)
    const isTimeStringInPast = useCallback((timeString, selectedDate) => {
        if (!selectedDate || !timeString) return false

        const parsedDate = parse(selectedDate, 'yyyy-MM-dd', new Date())
        if (!isToday(parsedDate)) return false

        // Parse time string to get hours and minutes
        const timeMatch = timeString.match(/(\d{1,2}):(\d{2})/)
        if (!timeMatch) return false

        const [, hourStr, minuteStr] = timeMatch
        const hours24 = parseInt(hourStr, 10)
        const minutes = parseInt(minuteStr, 10)

        // Get current time with 5-minute buffer
        const now = new Date()
        const bufferTime = new Date(now.getTime() + 5 * 60 * 1000) // Add 5 minutes
        const bufferHours = bufferTime.getHours()
        const bufferMinutes = bufferTime.getMinutes()

        // Compare times with buffer
        if (hours24 < bufferHours) return true
        if (hours24 === bufferHours && minutes < bufferMinutes) return true

        return false
    }, [])

    // Helper function to calculate next valid time (current time + 5 min buffer + round to 5-min increments)
    const getNextValidTimeString = useCallback(() => {
        const now = new Date()
        // Add 5 minutes buffer first
        now.setMinutes(now.getMinutes() + 5)

        // Round up to next 5-minute increment for better UX
        const minutes = now.getMinutes()
        const roundedMinutes = Math.ceil(minutes / 5) * 5
        now.setMinutes(roundedMinutes, 0, 0) // Also reset seconds and milliseconds

        // Handle hour rollover if minutes >= 60
        if (roundedMinutes >= 60) {
            now.setHours(now.getHours() + 1)
            now.setMinutes(roundedMinutes - 60)
        }

        return now.toTimeString().slice(0, 5) // Return in HH:MM format
    }, [])

    // Auto-adjust time when date changes to today and current time becomes invalid
    useEffect(() => {
        if (!selectedDate || !value) return

        // Only auto-adjust if current time is now in the past due to date change
        if (isTimeStringInPast(value, selectedDate)) {
            const nextValidTimeString = getNextValidTimeString()
            onChange?.(nextValidTimeString)
            setDisplayValue(nextValidTimeString)
        }
    }, [selectedDate, value])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const handleOpenPicker = useCallback(() => {
        setIsOpen(true)
    }, [])

    const handleClosePicker = useCallback(() => {
        setIsOpen(false)
    }, [])

    const handleTimeChange = useCallback(
        (newTime) => {
            setDisplayValue(newTime)
            onChange?.(newTime)
            setIsOpen(false)
        },
        [onChange],
    )

    return (
        <div ref={containerRef} className="tw:relative">
            <button
                onClick={handleOpenPicker}
                className={cn(
                    'tw:file:text-foreground tw:placeholder:text-muted-foreground tw:selection:bg-primary tw:selection:text-primary-foreground tw:dark:bg-background tw:border-[#3B5168] tw:flex tw:h-9 tw:w-full tw:min-w-0 tw:rounded-xs tw:border tw:bg-transparent tw:px-3 tw:py-1 tw:!text-sm tw:shadow-xs tw:transition-[color,box-shadow] tw:outline-none tw:file:inline-flex tw:file:h-7 tw:file:border-0 tw:file:bg-transparent tw:file:text-sm tw:file:font-medium tw:disabled:pointer-events-none tw:disabled:cursor-not-allowed tw:disabled:opacity-50 tw:md:text-sm tw:focus-visible:border-blue-500 tw:focus-visible:border-[1px] tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive tw:items-center tw:justify-between tw:text-white tw:cursor-pointer',
                    className,
                )}
                {...props}
            >
                <div className="tw:flex tw:items-center tw:gap-2">
                    <ClockIcon />
                    <span className="tw:text-white">{displayValue}</span>
                </div>
                <ChevronDownIcon />
            </button>

            {isOpen && (
                <TimePickerComponent
                    value={value}
                    onChange={handleTimeChange}
                    onCancel={handleClosePicker}
                    selectedDate={selectedDate}
                />
            )}
        </div>
    )
}

export { TimePicker }
